/**
 * ConfirmationScreen
 * ==================
 *
 * Full-screen (within the modal) success / confirmation screen shown after
 * an informant management action completes.  Features a large checkmark
 * icon, a headline, optional detail text, and a "Finish" button that
 * refreshes data and closes the modal.
 *
 * Matches the existing success patterns in the informant management forms
 * (green check icon + "Finish" CTA).
 */

import React from 'react';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/solid';
import ButtonSpinner from './ButtonSpinner';

/**
 * @param {Object}   props
 * @param {string}   props.headline      – Primary success message
 * @param {string}   [props.detail]      – Optional secondary text
 * @param {Function} props.onFinish      – Called when user clicks "Finish"
 * @param {boolean}  [props.isLoading]   – Show spinner on "Finish" button
 * @param {string}   [props.finishLabel] – Override the "Finish" button text
 */
export default function ConfirmationScreen({
  headline,
  detail,
  onFinish,
  isLoading = false,
  finishLabel = 'Finish',
}) {
  return (
    <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
      <CheckCircleSolid className="w-14 h-14 text-green-500 mb-4" aria-hidden="true" />

      <h3 className="text-sm font-semibold text-gray-900 mb-1">
        {headline}
      </h3>

      {detail && (
        <p className="text-xs text-gray-500 max-w-xs mb-6 break-words">{detail}</p>
      )}

      <button
        type="button"
        onClick={onFinish}
        disabled={isLoading}
        className={`
          px-5 py-2 text-sm font-semibold rounded-md transition-colors
          ${isLoading
            ? 'bg-gray-400 text-white cursor-not-allowed'
            : 'bg-violet-600 text-white hover:bg-violet-700'
          }
        `}
      >
        {isLoading ? (
          <span className="inline-flex items-center gap-2">
            <ButtonSpinner />
            Loading…
          </span>
        ) : finishLabel}
      </button>
    </div>
  );
}
